import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import { useToast } from '../../hooks/use-toast';
import { 
  Shield, Users, UserCog, Eye, AlertCircle, Plus, Trash2, Search, Loader2, 
  UserPlus, Building, User, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight,
  RefreshCw, KeyRound, UserX, UserCheck, Filter, X, Copy, Download
} from 'lucide-react';

const token = 'cookie-auth';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

const ROLES = [
  { value: 'client', label: 'Client', color: 'bg-blue-500', icon: '👤', description: 'Accès limité à ses propres opérations' },
  { value: 'admin', label: 'Administrateur', color: 'bg-red-500', icon: '👑', description: 'Accès complet, gestion des utilisateurs' },
  { value: 'operateur', label: 'Opérateur', color: 'bg-green-500', icon: '🔧', description: 'Accès multi-clients, modification des opérations' },
  { value: 'commercial', label: 'Commercial', color: 'bg-purple-500', icon: '💼', description: 'Accès multi-clients, gestion commerciale' },
  { value: 'auditeur', label: 'Auditeur', color: 'bg-orange-500', icon: '🔍', description: 'Accès multi-clients en lecture seule' },
  { value: 'field_ops', label: 'Field Ops', color: 'bg-teal-500', icon: '🚛', description: 'Opérations terrain, chargement/livraison' }
];

const PAGE_SIZES = [10, 20, 50, 100];

const getRoleBadge = (role) => {
  const roleConfig = ROLES.find(r => r.value === role) || ROLES[0];
  return (
    <Badge className={`${roleConfig.color} text-white`}>
      <span className="mr-1">{roleConfig.icon}</span>
      {roleConfig.label}
    </Badge>
  );
};

const UsersManagement = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  
  // Data state
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Pagination state
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  
  // Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [accountTypeFilter, setAccountTypeFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  
  // Selection state
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  
  // Action states
  const [updatingUserId, setUpdatingUserId] = useState(null);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  
  // Modal states
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newUser, setNewUser] = useState({
    email: '', firstName: '', lastName: '', phone: '',
    role: 'client', accountType: 'particulier', companyName: '', password: ''
  });
  
  // Bulk action dialogs
  const [showBulkRoleDialog, setShowBulkRoleDialog] = useState(false);
  const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);
  const [showBulkStatusDialog, setShowBulkStatusDialog] = useState(false);
  const [showBulkResetDialog, setShowBulkResetDialog] = useState(false);
  const [bulkNewRole, setBulkNewRole] = useState('client');
  const [bulkNewStatus, setBulkNewStatus] = useState(true);
  
  // Password reset
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [resetPasswordUser, setResetPasswordUser] = useState(null);
  const [resetPasswordResult, setResetPasswordResult] = useState(null);
  const [customPassword, setCustomPassword] = useState('');
  const [useCustomPassword, setUseCustomPassword] = useState(false);

  // Fetch users with debounce
  const fetchUsers = useCallback(async () => {
    try {
      if (!token) {
        navigate('/admin/login');
        return;
      }

      setLoading(true);
      
      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString(),
        sortBy: 'createdAt',
        sortOrder: 'desc'
      });
      
      if (searchQuery) params.append('search', searchQuery);
      if (roleFilter !== 'all') params.append('role', roleFilter);
      if (statusFilter !== 'all') params.append('isActive', statusFilter === 'active' ? 'true' : 'false');
      if (accountTypeFilter !== 'all') params.append('accountType', accountTypeFilter);

      const response = await axios.get(`${API_BASE_URL}/api/admin/users?${params}`, {
        headers: { 'Authorization': 'Bearer cookie-auth' }
      });

      setUsers(response.data.users);
      setTotal(response.data.total);
      setTotalPages(response.data.totalPages);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast({
        title: 'Erreur',
        description: error.response?.data?.detail || 'Erreur lors du chargement des utilisateurs',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, searchQuery, roleFilter, statusFilter, accountTypeFilter, navigate, toast]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchUsers();
    }, 300);
    return () => clearTimeout(timer);
  }, [fetchUsers]);

  // Reset selection when page changes
  useEffect(() => {
    setSelectedUsers([]);
    setSelectAll(false);
  }, [page, pageSize, searchQuery, roleFilter, statusFilter, accountTypeFilter]);

  // Handle select all
  const handleSelectAll = (checked) => {
    setSelectAll(checked);
    if (checked) {
      setSelectedUsers(users.map(u => u.id));
    } else {
      setSelectedUsers([]);
    }
  };

  // Handle individual selection
  const handleSelectUser = (userId, checked) => {
    if (checked) {
      setSelectedUsers([...selectedUsers, userId]);
    } else {
      setSelectedUsers(selectedUsers.filter(id => id !== userId));
      setSelectAll(false);
    }
  };

  // Clear filters
  const clearFilters = () => {
    setSearchQuery('');
    setRoleFilter('all');
    setStatusFilter('all');
    setAccountTypeFilter('all');
    setPage(1);
  };

  const hasActiveFilters = searchQuery || roleFilter !== 'all' || statusFilter !== 'all' || accountTypeFilter !== 'all';

  // API Helpers
  const getAuthHeaders = () => ({
    headers: {
      'Authorization': 'Bearer cookie-auth',
      'Content-Type': 'application/json'
    }
  });

  // Individual role change
  const handleRoleChange = async (userId, newRole) => {
    setUpdatingUserId(userId);
    try {
      const response = await axios.patch(
        `${API_BASE_URL}/api/admin/users/${userId}/role`,
        { role: newRole },
        getAuthHeaders()
      );

      setUsers(users.map(user => user.id === userId ? response.data : user));
      const roleConfig = ROLES.find(r => r.value === newRole);
      toast({ title: 'Rôle mis à jour', description: `Le rôle a été changé en ${roleConfig?.label}` });
    } catch (error) {
      toast({ title: 'Erreur', description: error.response?.data?.detail || 'Erreur', variant: 'destructive' });
    } finally {
      setUpdatingUserId(null);
    }
  };

  // Create user
  const handleCreateUser = async () => {
    if (!newUser.email || !newUser.firstName || !newUser.lastName || !newUser.password) {
      toast({ title: 'Champs requis', description: 'Veuillez remplir tous les champs obligatoires', variant: 'destructive' });
      return;
    }
    if (newUser.password.length < 6) {
      toast({ title: 'Mot de passe trop court', description: 'Minimum 6 caractères', variant: 'destructive' });
      return;
    }

    setCreating(true);
    try {
      await axios.post(`${API_BASE_URL}/api/admin/users`, newUser, getAuthHeaders());
      toast({ title: 'Utilisateur créé', description: `${newUser.firstName} ${newUser.lastName} a été créé` });
      setNewUser({ email: '', firstName: '', lastName: '', phone: '', role: 'client', accountType: 'particulier', companyName: '', password: '' });
      setIsCreateModalOpen(false);
      fetchUsers();
    } catch (error) {
      toast({ title: 'Erreur', description: error.response?.data?.detail || 'Erreur', variant: 'destructive' });
    } finally {
      setCreating(false);
    }
  };

  // Bulk role change
  const handleBulkRoleChange = async () => {
    if (selectedUsers.length === 0) return;
    setBulkActionLoading(true);
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/admin/users/bulk/role`,
        { userIds: selectedUsers, newRole: bulkNewRole },
        getAuthHeaders()
      );
      toast({ title: 'Rôles mis à jour', description: response.data.message });
      setShowBulkRoleDialog(false);
      setSelectedUsers([]);
      setSelectAll(false);
      fetchUsers();
    } catch (error) {
      toast({ title: 'Erreur', description: error.response?.data?.detail || 'Erreur', variant: 'destructive' });
    } finally {
      setBulkActionLoading(false);
    }
  };

  // Bulk status change
  const handleBulkStatusChange = async (activate) => {
    if (selectedUsers.length === 0) return;
    setBulkActionLoading(true);
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/admin/users/bulk/status`,
        { userIds: selectedUsers, isActive: activate },
        getAuthHeaders()
      );
      toast({ title: activate ? 'Utilisateurs activés' : 'Utilisateurs désactivés', description: response.data.message });
      setShowBulkStatusDialog(false);
      setSelectedUsers([]);
      setSelectAll(false);
      fetchUsers();
    } catch (error) {
      toast({ title: 'Erreur', description: error.response?.data?.detail || 'Erreur', variant: 'destructive' });
    } finally {
      setBulkActionLoading(false);
    }
  };

  // Bulk delete
  const handleBulkDelete = async () => {
    if (selectedUsers.length === 0) return;
    setBulkActionLoading(true);
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/admin/users/bulk/delete`,
        { userIds: selectedUsers },
        getAuthHeaders()
      );
      toast({ title: 'Utilisateurs supprimés', description: response.data.message });
      setShowBulkDeleteDialog(false);
      setSelectedUsers([]);
      setSelectAll(false);
      fetchUsers();
    } catch (error) {
      toast({ title: 'Erreur', description: error.response?.data?.detail || 'Erreur', variant: 'destructive' });
    } finally {
      setBulkActionLoading(false);
    }
  };

  // Single password reset
  const handleResetPassword = async () => {
    if (!resetPasswordUser) return;
    setBulkActionLoading(true);
    try {
      const payload = { userId: resetPasswordUser.id };
      if (useCustomPassword && customPassword) {
        payload.newPassword = customPassword;
      }
      
      const response = await axios.post(
        `${API_BASE_URL}/api/admin/users/reset-password`,
        payload,
        getAuthHeaders()
      );
      
      setResetPasswordResult(response.data);
      toast({ title: 'Mot de passe réinitialisé', description: response.data.message });
    } catch (error) {
      toast({ title: 'Erreur', description: error.response?.data?.detail || 'Erreur', variant: 'destructive' });
    } finally {
      setBulkActionLoading(false);
    }
  };

  // Bulk password reset
  const handleBulkResetPasswords = async () => {
    if (selectedUsers.length === 0) return;
    setBulkActionLoading(true);
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/admin/users/bulk/reset-password`,
        { userIds: selectedUsers },
        getAuthHeaders()
      );
      
      // Show results in a downloadable format
      const csvContent = response.data.results.map(r => `${r.email},${r.newPassword}`).join('\n');
      const blob = new Blob([`Email,Nouveau mot de passe\n${csvContent}`], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `passwords_reset_${new Date().toISOString().slice(0,10)}.csv`;
      a.click();
      
      toast({ title: 'Mots de passe réinitialisés', description: `${response.data.results.length} mot(s) de passe réinitialisé(s). Fichier CSV téléchargé.` });
      setShowBulkResetDialog(false);
      setSelectedUsers([]);
      setSelectAll(false);
    } catch (error) {
      toast({ title: 'Erreur', description: error.response?.data?.detail || 'Erreur', variant: 'destructive' });
    } finally {
      setBulkActionLoading(false);
    }
  };

  // Copy to clipboard
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast({ title: 'Copié', description: 'Mot de passe copié dans le presse-papiers' });
  };

  if (loading && users.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-[#1db584] mx-auto" />
          <p className="mt-4 text-gray-600">Chargement des utilisateurs...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-lg md:text-2xl font-bold text-gray-900 flex items-center gap-2">
              <UserCog className="w-7 h-7 text-[#1db584]" />
              Gestion des Utilisateurs & Rôles
            </h1>
            <p className="text-gray-600 text-sm mt-1">
              {total} utilisateur(s) au total
            </p>
          </div>
          <div className="flex gap-2">
            <Dialog open={isCreateModalOpen} onOpenChange={setIsCreateModalOpen}>
              <DialogTrigger asChild>
                <Button className="bg-[#1db584] hover:bg-[#19a074]">
                  <Plus className="w-4 h-4 mr-2" />
                  Nouvel utilisateur
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <UserPlus className="w-5 h-5 text-[#1db584]" />
                    Créer un nouvel utilisateur
                  </DialogTitle>
                  <DialogDescription>
                    Remplissez les informations pour créer un nouvel utilisateur
                  </DialogDescription>
                </DialogHeader>
                
                <div className="grid gap-4 py-4 max-h-[60vh] overflow-y-auto">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="firstName">Prénom *</Label>
                      <Input id="firstName" value={newUser.firstName} onChange={(e) => setNewUser({...newUser, firstName: e.target.value})} placeholder="Jean" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="lastName">Nom *</Label>
                      <Input id="lastName" value={newUser.lastName} onChange={(e) => setNewUser({...newUser, lastName: e.target.value})} placeholder="Dupont" />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="email">Email *</Label>
                    <Input id="email" type="email" value={newUser.email} onChange={(e) => setNewUser({...newUser, email: e.target.value})} placeholder="jean.dupont@example.com" />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="phone">Téléphone</Label>
                    <Input id="phone" value={newUser.phone} onChange={(e) => setNewUser({...newUser, phone: e.target.value})} placeholder="+225 07 00 00 00" />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Type de compte</Label>
                    <div className="flex gap-2">
                      <Button type="button" variant={newUser.accountType === 'particulier' ? 'default' : 'outline'} className={newUser.accountType === 'particulier' ? 'bg-[#1db584] hover:bg-[#19a074]' : ''} onClick={() => setNewUser({...newUser, accountType: 'particulier', companyName: ''})}>
                        <User className="w-4 h-4 mr-2" />Particulier
                      </Button>
                      <Button type="button" variant={newUser.accountType === 'entreprise' ? 'default' : 'outline'} className={newUser.accountType === 'entreprise' ? 'bg-[#1db584] hover:bg-[#19a074]' : ''} onClick={() => setNewUser({...newUser, accountType: 'entreprise'})}>
                        <Building className="w-4 h-4 mr-2" />Entreprise
                      </Button>
                    </div>
                  </div>
                  
                  {newUser.accountType === 'entreprise' && (
                    <div className="space-y-2">
                      <Label htmlFor="companyName">Nom de l'entreprise</Label>
                      <Input id="companyName" value={newUser.companyName} onChange={(e) => setNewUser({...newUser, companyName: e.target.value})} placeholder="Nom de l'entreprise" />
                    </div>
                  )}
                  
                  <div className="space-y-2">
                    <Label>Rôle *</Label>
                    <Select value={newUser.role} onValueChange={(value) => setNewUser({...newUser, role: value})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {ROLES.map(role => (
                          <SelectItem key={role.value} value={role.value}>
                            <span className="flex items-center gap-2"><span>{role.icon}</span>{role.label}</span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="password">Mot de passe *</Label>
                    <Input id="password" type="password" value={newUser.password} onChange={(e) => setNewUser({...newUser, password: e.target.value})} placeholder="Minimum 6 caractères" />
                  </div>
                </div>
                
                <DialogFooter>
                  <Button variant="outline" onClick={() => setIsCreateModalOpen(false)}>Annuler</Button>
                  <Button onClick={handleCreateUser} disabled={creating} className="bg-[#1db584] hover:bg-[#19a074]">
                    {creating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Création...</> : <><Plus className="w-4 h-4 mr-2" />Créer</>}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            
            <Button onClick={() => navigate('/admin/settings?tab=audit')} variant="outline">
              <Eye className="w-4 h-4 mr-2" />Logs d'audit
            </Button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <Card className="mb-4">
        <CardContent className="pt-4">
          <div className="flex flex-col gap-4">
            <div className="flex flex-wrap gap-4 items-center">
              <div className="flex-1 min-w-[200px] relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  placeholder="Rechercher par nom, email, téléphone..."
                  value={searchQuery}
                  onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
                  className="pl-10"
                />
              </div>
              
              <Select value={roleFilter} onValueChange={(v) => { setRoleFilter(v); setPage(1); }}>
                <SelectTrigger className="w-[150px]"><SelectValue placeholder="Rôle" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les rôles</SelectItem>
                  {ROLES.map(role => (
                    <SelectItem key={role.value} value={role.value}><span className="flex items-center gap-2"><span>{role.icon}</span>{role.label}</span></SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
                <SelectTrigger className="w-[130px]"><SelectValue placeholder="Statut" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous</SelectItem>
                  <SelectItem value="active">Actifs</SelectItem>
                  <SelectItem value="inactive">Inactifs</SelectItem>
                </SelectContent>
              </Select>
              
              <Select value={accountTypeFilter} onValueChange={(v) => { setAccountTypeFilter(v); setPage(1); }}>
                <SelectTrigger className="w-[150px]"><SelectValue placeholder="Type" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous types</SelectItem>
                  <SelectItem value="particulier">Particulier</SelectItem>
                  <SelectItem value="entreprise">Entreprise</SelectItem>
                </SelectContent>
              </Select>
              
              {hasActiveFilters && (
                <Button variant="ghost" size="sm" onClick={clearFilters} className="text-gray-500">
                  <X className="w-4 h-4 mr-1" />Effacer
                </Button>
              )}
              
              <Button variant="outline" size="sm" onClick={fetchUsers}>
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Bulk Actions Bar */}
      {selectedUsers.length > 0 && (
        <Card className="mb-4 border-[#1db584] bg-[#1db584]/5">
          <CardContent className="py-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <span className="font-medium text-[#1db584]">
                {selectedUsers.length} utilisateur(s) sélectionné(s)
              </span>
              <div className="flex gap-2 flex-wrap">
                <Button size="sm" variant="outline" onClick={() => setShowBulkRoleDialog(true)}>
                  <Shield className="w-4 h-4 mr-1" />Changer rôle
                </Button>
                <Button size="sm" variant="outline" onClick={() => { setBulkNewStatus(true); setShowBulkStatusDialog(true); }} className="text-green-600 border-green-300">
                  <UserCheck className="w-4 h-4 mr-1" />Activer
                </Button>
                <Button size="sm" variant="outline" onClick={() => { setBulkNewStatus(false); setShowBulkStatusDialog(true); }} className="text-orange-600 border-orange-300">
                  <UserX className="w-4 h-4 mr-1" />Désactiver
                </Button>
                <Button size="sm" variant="outline" onClick={() => setShowBulkResetDialog(true)} className="text-blue-600 border-blue-300">
                  <KeyRound className="w-4 h-4 mr-1" />Reset MDP
                </Button>
                <Button size="sm" variant="destructive" onClick={() => setShowBulkDeleteDialog(true)}>
                  <Trash2 className="w-4 h-4 mr-1" />Supprimer
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Users Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left">
                    <Checkbox checked={selectAll} onCheckedChange={handleSelectAll} />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Utilisateur</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contact</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rôle</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {users.map((user) => (
                  <tr key={user.id} className={`hover:bg-gray-50 ${selectedUsers.includes(user.id) ? 'bg-[#1db584]/5' : ''}`}>
                    <td className="px-4 py-3">
                      <Checkbox 
                        checked={selectedUsers.includes(user.id)} 
                        onCheckedChange={(checked) => handleSelectUser(user.id, checked)} 
                      />
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-medium text-gray-900">{user.firstName} {user.lastName}</p>
                        <p className="text-xs text-gray-500">{user.clientNumber || 'N/A'}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        <p className="text-sm text-gray-900">{user.email}</p>
                        <p className="text-xs text-gray-500">{user.phone || '-'}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant="outline" className="capitalize text-xs">
                        {user.accountType === 'particulier' ? '👤' : '🏢'} {user.accountType}
                      </Badge>
                    </td>
                    <td className="px-4 py-3">
                      <Select value={user.role} onValueChange={(newRole) => handleRoleChange(user.id, newRole)} disabled={updatingUserId === user.id}>
                        <SelectTrigger className="w-[140px] h-8 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {ROLES.map(role => (
                            <SelectItem key={role.value} value={role.value}>
                              <span className="flex items-center gap-1"><span>{role.icon}</span>{role.label}</span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-4 py-3">
                      {user.isActive !== false ? (
                        <Badge className="bg-green-500 text-xs">Actif</Badge>
                      ) : (
                        <Badge className="bg-gray-400 text-xs">Inactif</Badge>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-blue-600" onClick={() => { setResetPasswordUser(user); setResetPasswordResult(null); setCustomPassword(''); setUseCustomPassword(false); setShowResetPasswordModal(true); }}>
                          <KeyRound className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-red-600" onClick={() => { setSelectedUsers([user.id]); setShowBulkDeleteDialog(true); }}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {users.length === 0 && (
            <div className="text-center py-12">
              <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">Aucun utilisateur trouvé</p>
              {hasActiveFilters && (
                <Button variant="link" onClick={clearFilters} className="mt-2">Réinitialiser les filtres</Button>
              )}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 0 && (
            <div className="flex items-center justify-between px-4 py-3 border-t bg-gray-50">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <span>Afficher</span>
                <Select value={pageSize.toString()} onValueChange={(v) => { setPageSize(parseInt(v)); setPage(1); }}>
                  <SelectTrigger className="w-[70px] h-8"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PAGE_SIZES.map(size => <SelectItem key={size} value={size.toString()}>{size}</SelectItem>)}
                  </SelectContent>
                </Select>
                <span>par page</span>
              </div>
              
              <div className="flex items-center gap-1">
                <Button variant="outline" size="sm" onClick={() => setPage(1)} disabled={page === 1}>
                  <ChevronsLeft className="w-4 h-4" />
                </Button>
                <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="px-3 text-sm">
                  Page {page} / {totalPages}
                </span>
                <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}>
                  <ChevronRight className="w-4 h-4" />
                </Button>
                <Button variant="outline" size="sm" onClick={() => setPage(totalPages)} disabled={page >= totalPages}>
                  <ChevronsRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Bulk Role Change Dialog */}
      <AlertDialog open={showBulkRoleDialog} onOpenChange={setShowBulkRoleDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Changer le rôle en masse</AlertDialogTitle>
            <AlertDialogDescription>
              Vous allez changer le rôle de {selectedUsers.length} utilisateur(s).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4">
            <Label>Nouveau rôle</Label>
            <Select value={bulkNewRole} onValueChange={setBulkNewRole}>
              <SelectTrigger className="mt-2"><SelectValue /></SelectTrigger>
              <SelectContent>
                {ROLES.map(role => (
                  <SelectItem key={role.value} value={role.value}>
                    <span className="flex items-center gap-2"><span>{role.icon}</span>{role.label}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleBulkRoleChange} disabled={bulkActionLoading} className="bg-[#1db584]">
              {bulkActionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Confirmer'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Status Change Dialog */}
      <AlertDialog open={showBulkStatusDialog} onOpenChange={setShowBulkStatusDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{bulkNewStatus ? 'Activer' : 'Désactiver'} les utilisateurs</AlertDialogTitle>
            <AlertDialogDescription>
              Vous allez {bulkNewStatus ? 'activer' : 'désactiver'} {selectedUsers.length} utilisateur(s).
              {!bulkNewStatus && " Les utilisateurs désactivés ne pourront plus se connecter."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={() => handleBulkStatusChange(bulkNewStatus)} disabled={bulkActionLoading} className={bulkNewStatus ? 'bg-green-600' : 'bg-orange-600'}>
              {bulkActionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Confirmer'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Delete Dialog */}
      <AlertDialog open={showBulkDeleteDialog} onOpenChange={setShowBulkDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer les utilisateurs</AlertDialogTitle>
            <AlertDialogDescription>
              Vous allez supprimer définitivement {selectedUsers.length} utilisateur(s). Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleBulkDelete} disabled={bulkActionLoading} className="bg-red-600">
              {bulkActionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Supprimer'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Reset Password Dialog */}
      <AlertDialog open={showBulkResetDialog} onOpenChange={setShowBulkResetDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Réinitialiser les mots de passe</AlertDialogTitle>
            <AlertDialogDescription>
              Vous allez réinitialiser les mots de passe de {selectedUsers.length} utilisateur(s). 
              Des mots de passe aléatoires seront générés et un fichier CSV sera téléchargé.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleBulkResetPasswords} disabled={bulkActionLoading} className="bg-blue-600">
              {bulkActionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Download className="w-4 h-4 mr-2" />Générer & Télécharger</>}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Single Password Reset Modal */}
      <Dialog open={showResetPasswordModal} onOpenChange={setShowResetPasswordModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-blue-600" />
              Réinitialiser le mot de passe
            </DialogTitle>
            <DialogDescription>
              {resetPasswordUser && `Utilisateur: ${resetPasswordUser.firstName} ${resetPasswordUser.lastName} (${resetPasswordUser.email})`}
            </DialogDescription>
          </DialogHeader>
          
          {!resetPasswordResult ? (
            <div className="space-y-4 py-4">
              <div className="flex items-center space-x-2">
                <Checkbox id="customPwd" checked={useCustomPassword} onCheckedChange={setUseCustomPassword} />
                <Label htmlFor="customPwd">Définir un mot de passe personnalisé</Label>
              </div>
              
              {useCustomPassword && (
                <div className="space-y-2">
                  <Label htmlFor="newPassword">Nouveau mot de passe</Label>
                  <Input id="newPassword" type="password" value={customPassword} onChange={(e) => setCustomPassword(e.target.value)} placeholder="Minimum 6 caractères" />
                </div>
              )}
              
              {!useCustomPassword && (
                <p className="text-sm text-gray-500">Un mot de passe aléatoire sécurisé sera généré.</p>
              )}
            </div>
          ) : (
            <div className="space-y-4 py-4">
              <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                <p className="text-green-800 font-medium mb-2">✅ Mot de passe réinitialisé avec succès</p>
                {resetPasswordResult.generatedPassword && (
                  <div className="flex items-center gap-2 mt-3">
                    <code className="flex-1 p-2 bg-white rounded border text-sm font-mono">
                      {resetPasswordResult.generatedPassword}
                    </code>
                    <Button size="sm" variant="outline" onClick={() => copyToClipboard(resetPasswordResult.generatedPassword)}>
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                )}
                {resetPasswordResult.passwordProvided && (
                  <p className="text-sm text-green-700 mt-2">Le mot de passe personnalisé a été défini.</p>
                )}
              </div>
            </div>
          )}
          
          <DialogFooter>
            {!resetPasswordResult ? (
              <>
                <Button variant="outline" onClick={() => setShowResetPasswordModal(false)}>Annuler</Button>
                <Button onClick={handleResetPassword} disabled={bulkActionLoading || (useCustomPassword && customPassword.length < 6)} className="bg-blue-600">
                  {bulkActionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Réinitialiser'}
                </Button>
              </>
            ) : (
              <Button onClick={() => { setShowResetPasswordModal(false); setResetPasswordResult(null); }}>Fermer</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UsersManagement;
